/**
 * example of filterConfig:
 * [
 *    { name: "investigator", prop: "investigator", noneOption: true },
 *    { name: "operator", prop: "operator", multiple: true },
 *    { name: "studyDirector", prop: "study_director", noneOption: true },
 *    { name: "taskGroup", prop: "task_group_name" },
 *    { name: "readoutGroup", prop: "readout_group_name" },
 *    { name: "status", prop: "status" },
 *    { name: "siteCountry", prop: "site_country", noneOption: true },
 *    { name: "availableInExperimentSetup", prop: "isAvailableInExperimentSetup", defaults: [true]},
 *  ]
 */
import _ from "lodash";

export type FilterConfigItem = {
    name: string;
    prop: string;
    multiple?: boolean;
    noneOption?: boolean;
    defaults?: any[];
};

export type FilterConfig = FilterConfigItem[];

export type FilterOptions = Record<string, FilterOptionValues>;

export type FilterOptionValues = {
    values: string[];
    defaults?: string[];
};

export function buildFilterOptionsFromData(data: any, filterConfig: FilterConfig): FilterOptions {
    const filterOptions: FilterOptions = {};
    filterConfig.forEach((config: FilterConfigItem) => {
        let availableValuesToFilterBy: any[] = data
            .map((item) => item[config.prop])
            .filter((value) => !_.isNil(value));
        if (config.multiple) {
            availableValuesToFilterBy = availableValuesToFilterBy.reduce((acc, val) => {
                if (val) {
                    acc.push(...val);
                }
                return acc;
            }, []);
        }
        availableValuesToFilterBy = [...new Set(availableValuesToFilterBy)].sort(alphaSortFn);
        availableValuesToFilterBy = availableValuesToFilterBy.filter((val) => val !== "");
        if (config.noneOption) {
            availableValuesToFilterBy.push("None");
        }
        filterOptions[config.name] = {
            values: availableValuesToFilterBy,
        };
        if (config.defaults) {
            filterOptions[config.name].defaults = config.defaults;
        }
    });

    return filterOptions;
}

export function buildEmptyFilterOptions(filterConfig: FilterConfig) {
    const filterNames = filterConfig.map((config: FilterConfigItem) => config.name);
    return filterNames.reduce((acc, name) => {
        acc[name] = [];
        return acc;
    }, {});
}

export type Filters = Record<string, any[]>;

export function filterData<T>(data: T[], filters: Filters, filterConfig: FilterConfig): T[] {
    let filteredItems: T[] = data;
    Object.keys(filters).forEach((filterName: string) => {
        const filterValues = filters[filterName];
        if (filterValues.length > 0) {
            const filterConfigItem = filterConfig.find((config: FilterConfigItem) => {
                return config.name === filterName;
            });
            if (_.isNil(filterConfigItem)) {
                throw new Error(`Filter configuration doesn't exist for ${filterName}`);
            }
            const filteredProp = filterConfigItem.prop;
            if (filterValues.includes("None")) {
                filteredItems = filteredItems.filter(
                    (item: T) =>
                        filterValues.some((filterValue) =>
                            isMatchingFilterValue(filterValue, item[filteredProp])
                        ) ||
                        _.isNil(item[filteredProp]) ||
                        item[filteredProp].length === 0
                );
            } else if (filterConfigItem.multiple) {
                filteredItems = filteredItems.filter((item) =>
                    filterValues.some(
                        (filterValue) =>
                            item[filteredProp] &&
                            item[filteredProp].some((value) =>
                                isMatchingFilterValue(filterValue, value)
                            )
                    )
                );
            } else {
                filteredItems = filteredItems.filter((item) =>
                    filterValues.some((filterValue) =>
                        isMatchingFilterValue(filterValue, item[filteredProp])
                    )
                );
            }
        }
    });
    return filteredItems;
}

function isMatchingFilterValue(filterValue: unknown, itemValue: unknown) {
    if (filterValue === "true") {
        return itemValue === "true" || itemValue === true;
    } else if (filterValue === "false") {
        return itemValue === "false" || itemValue === false;
    } else {
        return filterValue === itemValue;
    }
}

function alphaSortFn(a, b) {
    if (parseFloat(a) && parseFloat(b)) {
        return parseFloat(a) - parseFloat(b);
    }

    return (a || "")
        .toString()
        .toLowerCase()
        .localeCompare((b || "").toString().toLowerCase());
}
